import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../supabase";
import { TabsBar } from "../components/TabsBar";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type RouteRow = {
  id: number;
  name: string;
  name_ur: string;
  color: string;
  active: boolean;
};

type RouteStopRow = {
  route_id: number;
  stop_id: number;
  seq: number;
  dist_along_shape_m: number;
  routes: { name: string } | null;
  stops: { name: string; name_ur: string } | null;
};

type ScheduleRow = {
  id: number;
  route_id: number;
  trip_no: number;
  departure_time: string | null;
  prayer_anchor: string | null;
  prayer_offset_min: number;
  days_mask: number;
  schedule_set: string;
  suspended: boolean;
  routes: { name: string } | null;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDaysMask(mask: number): string {
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const bits = [1, 2, 4, 8, 16, 32, 64];
  const active = bits
    .map((b, i) => ((mask & b) !== 0 ? days[i] : null))
    .filter(Boolean) as string[];
  if (active.length === 7) return "Daily";
  if (active.length === 5 && (mask & 31) === 31) return "Mon–Fri";
  if (active.length === 2 && (mask & 96) === 96) return "Sat–Sun";
  return active.join(", ");
}

function formatDeparture(s: ScheduleRow): string {
  if (s.departure_time) return s.departure_time.slice(0, 5);
  if (s.prayer_anchor) {
    const offset = s.prayer_offset_min > 0 ? ` +${s.prayer_offset_min}m` : "";
    return `After ${s.prayer_anchor}${offset}`;
  }
  return "—";
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function RoutesPage() {
  const [routes, setRoutes] = useState<RouteRow[]>([]);
  const [routeStops, setRouteStops] = useState<RouteStopRow[]>([]);
  const [schedules, setSchedules] = useState<ScheduleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [rRes, rsRes, sRes] = await Promise.all([
        supabase
          .from("routes")
          .select("id, name, name_ur, color, active")
          .order("id", { ascending: true }),
        supabase
          .from("route_stops")
          .select(
            "route_id, stop_id, seq, dist_along_shape_m, routes(name), stops(name, name_ur)",
          )
          .order("seq", { ascending: true }),
        supabase
          .from("schedules")
          .select(
            "id, route_id, trip_no, departure_time, prayer_anchor, prayer_offset_min, days_mask, schedule_set, suspended, routes(name)",
          )
          .order("route_id", { ascending: true })
          .order("trip_no", { ascending: true }),
      ]);
      if (rRes.error) throw rRes.error;
      if (rsRes.error) throw rsRes.error;
      if (sRes.error) throw sRes.error;
      setRoutes((rRes.data ?? []) as RouteRow[]);
      setRouteStops((rsRes.data ?? []) as unknown as RouteStopRow[]);
      setSchedules((sRes.data ?? []) as unknown as ScheduleRow[]);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <main style={styles.page}>
      <header style={styles.header}>
        <div>
          <h1 style={styles.h1}>Routes &amp; schedules</h1>
          <p style={styles.muted}>
            Read-only view of routes, stops, and timetables.
          </p>
        </div>
        <div style={styles.headerRight}>
          <Link to="/dashboard" style={styles.linkButton}>
            ← Dashboard
          </Link>
        </div>
      </header>

      <TabsBar active="routes" />

      {error && (
        <div
          style={{
            ...styles.alert,
            background: "#fdecea",
            color: "#a02622",
            borderColor: "#e0a3a0",
          }}
        >
          {error}
        </div>
      )}

      {/* Routes */}
      <section style={styles.card}>
        <h2 style={styles.h2}>Routes</h2>
        {loading ? (
          <p style={styles.muted}>Loading…</p>
        ) : routes.length === 0 ? (
          <p style={styles.muted}>No routes defined.</p>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Colour</th>
                <th style={styles.th}>Active</th>
              </tr>
            </thead>
            <tbody>
              {routes.map((r) => (
                <tr key={r.id}>
                  <td style={styles.td}>{r.id}</td>
                  <td style={styles.td}>
                    <div style={styles.nameCell}>
                      <strong>{r.name}</strong>
                      <span style={styles.nameUr} dir="rtl">
                        {r.name_ur}
                      </span>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <span
                      style={{ ...styles.swatch, background: r.color }}
                      aria-label={`Colour ${r.color}`}
                    />
                    <code style={styles.muted}>{r.color}</code>
                  </td>
                  <td style={styles.td}>
                    <span
                      style={{
                        ...styles.badge,
                        background: r.active ? "#1f9d55" : "#9aa0a6",
                      }}
                    >
                      {r.active ? "ACTIVE" : "inactive"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Stops */}
      <section style={styles.card}>
        <h2 style={styles.h2}>Stops by route</h2>
        {loading ? (
          <p style={styles.muted}>Loading…</p>
        ) : routeStops.length === 0 ? (
          <p style={styles.muted}>No stops mapped to routes.</p>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Route</th>
                <th style={styles.th}>Seq</th>
                <th style={styles.th}>Stop</th>
                <th style={styles.th}>Distance (m)</th>
              </tr>
            </thead>
            <tbody>
              {routeStops.map((rs) => (
                <tr key={`${rs.route_id}-${rs.stop_id}`}>
                  <td style={styles.td}>
                    {rs.routes?.name ?? `Route ${rs.route_id}`}
                  </td>
                  <td style={styles.td}>{rs.seq}</td>
                  <td style={styles.td}>
                    <div style={styles.nameCell}>
                      <strong>
                        {rs.stops?.name ?? `Stop ${rs.stop_id}`}
                      </strong>
                      <span style={styles.nameUr} dir="rtl">
                        {rs.stops?.name_ur}
                      </span>
                    </div>
                  </td>
                  <td style={styles.td}>{rs.dist_along_shape_m}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Schedules */}
      <section style={styles.card}>
        <h2 style={styles.h2}>Schedules</h2>
        {loading ? (
          <p style={styles.muted}>Loading…</p>
        ) : schedules.length === 0 ? (
          <p style={styles.muted}>No schedules defined.</p>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Route</th>
                <th style={styles.th}>Trip #</th>
                <th style={styles.th}>Departure</th>
                <th style={styles.th}>Days</th>
                <th style={styles.th}>Set</th>
                <th style={styles.th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {schedules.map((s) => (
                <tr key={s.id}>
                  <td style={styles.td}>
                    {s.routes?.name ?? `Route ${s.route_id}`}
                  </td>
                  <td style={styles.td}>{s.trip_no}</td>
                  <td style={styles.td}>{formatDeparture(s)}</td>
                  <td style={styles.td}>{formatDaysMask(s.days_mask)}</td>
                  <td style={styles.td}>
                    <span style={styles.scheduleSetBadge}>
                      {s.schedule_set}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span
                      style={{
                        ...styles.badge,
                        background: s.suspended ? "#c0392b" : "#1f9d55",
                      }}
                    >
                      {s.suspended ? "SUSPENDED" : "active"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </main>
  );
}

export default RoutesPage;

// ---------------------------------------------------------------------------
// Styles — inline (zero extra deps)
// ---------------------------------------------------------------------------

const styles: Record<string, React.CSSProperties> = {
  page: {
    fontFamily: "system-ui, -apple-system, sans-serif",
    maxWidth: 960,
    margin: "0 auto",
    padding: 24,
    color: "#1a1a1a",
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 16,
  },
  headerRight: { display: "flex", alignItems: "center", gap: 12 },
  h1: { margin: 0, fontSize: 20 },
  h2: { margin: 0, fontSize: 16 },
  muted: { color: "#666", fontSize: 14, margin: 0 },
  alert: {
    padding: "10px 14px",
    border: "1px solid",
    borderRadius: 6,
    marginBottom: 16,
    fontWeight: 600,
  },
  card: {
    padding: 20,
    border: "1px solid #ddd",
    borderRadius: 8,
    background: "#fff",
    marginBottom: 20,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  linkButton: {
    padding: "6px 10px",
    background: "#f1f1f1",
    color: "#1a1a1a",
    border: "1px solid #ccc",
    borderRadius: 4,
    textDecoration: "none",
    fontSize: 13,
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 14 },
  th: {
    textAlign: "left",
    padding: "8px 10px",
    borderBottom: "2px solid #1a3a5c",
  },
  td: {
    padding: "8px 10px",
    borderBottom: "1px solid #eee",
    verticalAlign: "middle",
  },
  badge: {
    display: "inline-block",
    padding: "2px 8px",
    color: "#fff",
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: 0.5,
  },
  swatch: {
    display: "inline-block",
    width: 22,
    height: 22,
    border: "1px solid #ccc",
    borderRadius: 4,
  },
  nameCell: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  nameUr: { fontSize: 13, color: "#666" },
  scheduleSetBadge: {
    display: "inline-block",
    padding: "2px 8px",
    background: "#e7eef6",
    color: "#1a3a5c",
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
};
